import type { AccountBilling } from "@/domain/model/AccountBilling";
import type { CancelAccountBilling } from "@/domain/usecase/CancelAccountBilling";
import type { DecreaseAccountSeats } from "@/domain/usecase/DecreaseAccountSeats";
import type { GetAccountBilling } from "@/domain/usecase/GetAccountBilling";
import type { GetBillingPortalLink } from "@/domain/usecase/GetBillingPortalLink";
import type { IncreaseAccountSeats } from "@/domain/usecase/IncreaseAccountSeats";
import type { ReactivateAccountBilling } from "@/domain/usecase/ReactivateAccountBilling";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

type ActionKind =
  | "increaseSeats"
  | "decreaseSeats"
  | "cancel"
  | "reactivate"
  | "portal"
  | null;

export interface BillingViewModelState {
  data: AccountBilling | null;
  isInitialLoading: boolean;
  isRefreshing: boolean;
  loadError: string | null;
  actionError: string | null;
  successMessage: string | null;
  activeAction: ActionKind;
  targetSeatQuantityInput: string;
}

type Action =
  | { type: "LOAD_START" }
  | { type: "LOAD_SUCCESS"; data: AccountBilling }
  | { type: "LOAD_ERROR"; message: string }
  | { type: "REFRESH_START" }
  | {
      type: "REFRESH_SUCCESS";
      data: AccountBilling;
      successMessage?: string | null;
    }
  | { type: "REFRESH_ERROR"; message: string }
  | { type: "SET_TARGET_SEAT_QUANTITY_INPUT"; value: string }
  | { type: "ACTION_START"; action: Exclude<ActionKind, null> }
  | { type: "ACTION_ERROR"; message: string }
  | { type: "CLEAR_MESSAGES" };

const initialState: BillingViewModelState = {
  data: null,
  isInitialLoading: true,
  isRefreshing: false,
  loadError: null,
  actionError: null,
  successMessage: null,
  activeAction: null,
  targetSeatQuantityInput: "",
};

function reducer(
  state: BillingViewModelState,
  action: Action,
): BillingViewModelState {
  switch (action.type) {
    case "LOAD_START":
      return {
        ...state,
        isInitialLoading: true,
        loadError: null,
      };

    case "LOAD_SUCCESS":
      return {
        ...state,
        data: action.data,
        isInitialLoading: false,
        isRefreshing: false,
        loadError: null,
        actionError: null,
        activeAction: null,
        targetSeatQuantityInput: String(
          action.data.subscription.purchasedSeatQuantity,
        ),
      };

    case "LOAD_ERROR":
      return {
        ...state,
        isInitialLoading: false,
        isRefreshing: false,
        loadError: action.message,
      };

    case "REFRESH_START":
      return {
        ...state,
        isRefreshing: true,
        actionError: null,
        successMessage: null,
      };

    case "REFRESH_SUCCESS":
      return {
        ...state,
        data: action.data,
        isRefreshing: false,
        actionError: null,
        activeAction: null,
        successMessage: action.successMessage ?? null,
        targetSeatQuantityInput: String(
          action.data.subscription.purchasedSeatQuantity,
        ),
      };

    case "REFRESH_ERROR":
      return {
        ...state,
        isRefreshing: false,
        activeAction: null,
        actionError: action.message,
      };

    case "SET_TARGET_SEAT_QUANTITY_INPUT":
      return {
        ...state,
        targetSeatQuantityInput: action.value,
      };

    case "ACTION_START":
      return {
        ...state,
        activeAction: action.action,
        actionError: null,
        successMessage: null,
      };

    case "ACTION_ERROR":
      return {
        ...state,
        activeAction: null,
        isRefreshing: false,
        actionError: action.message,
      };

    case "CLEAR_MESSAGES":
      return {
        ...state,
        actionError: null,
        successMessage: null,
      };

    default:
      return state;
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function toUserMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return "Die Aktion konnte nicht abgeschlossen werden.";
}

interface UseBillingViewModelParams {
  getAccountBilling: GetAccountBilling;
  increaseAccountSeats: IncreaseAccountSeats;
  decreaseAccountSeats: DecreaseAccountSeats;
  cancelAccountBilling: CancelAccountBilling;
  reactivateAccountBilling: ReactivateAccountBilling;
  getBillingPortalLink: GetBillingPortalLink;
}

export function useBillingViewModel({
  getAccountBilling,
  increaseAccountSeats,
  decreaseAccountSeats,
  cancelAccountBilling,
  reactivateAccountBilling,
  getBillingPortalLink,
}: UseBillingViewModelParams) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchControllerRef = useRef<AbortController | null>(null);
  const actionControllerRef = useRef<AbortController | null>(null);
  const fetchSequenceRef = useRef(0);

  const abortCurrentFetch = useCallback(() => {
    fetchControllerRef.current?.abort();
    fetchControllerRef.current = null;
  }, []);

  const abortCurrentAction = useCallback(() => {
    actionControllerRef.current?.abort();
    actionControllerRef.current = null;
  }, []);

  const runFetch = useCallback(
    async ({
      mode,
      successMessage,
    }: {
      mode: "initial" | "refresh";
      successMessage?: string;
    }) => {
      abortCurrentFetch();

      const controller = new AbortController();
      fetchControllerRef.current = controller;
      const sequence = ++fetchSequenceRef.current;

      if (mode === "initial") {
        dispatch({ type: "LOAD_START" });
      } else {
        dispatch({ type: "REFRESH_START" });
      }

      try {
        const data = await getAccountBilling.execute(controller.signal);

        if (controller.signal.aborted) return;
        if (fetchControllerRef.current !== controller) return;
        if (sequence !== fetchSequenceRef.current) return;

        if (mode === "initial") {
          dispatch({ type: "LOAD_SUCCESS", data });
        } else {
          dispatch({ type: "REFRESH_SUCCESS", data, successMessage });
        }
      } catch (error) {
        if (isAbortError(error)) return;
        if (fetchControllerRef.current !== controller) return;
        if (sequence !== fetchSequenceRef.current) return;

        const message = toUserMessage(error);

        if (mode === "initial") {
          dispatch({ type: "LOAD_ERROR", message });
        } else {
          dispatch({ type: "REFRESH_ERROR", message });
        }
      } finally {
        if (fetchControllerRef.current === controller) {
          fetchControllerRef.current = null;
        }
      }
    },
    [abortCurrentFetch, getAccountBilling],
  );

  useEffect(() => {
    void runFetch({ mode: "initial" });

    return () => {
      abortCurrentFetch();
      abortCurrentAction();
    };
  }, [abortCurrentAction, abortCurrentFetch, runFetch]);

  const refresh = useCallback(
    async (successMessage?: string) => {
      await runFetch({ mode: "refresh", successMessage });
    },
    [runFetch],
  );

  const setTargetSeatQuantityInput = useCallback((value: string) => {
    dispatch({ type: "SET_TARGET_SEAT_QUANTITY_INPUT", value });
  }, []);

  const runAction = useCallback(
    async (
      action: Exclude<ActionKind, null>,
      execute: (signal: AbortSignal) => Promise<void>,
    ) => {
      abortCurrentAction();

      const controller = new AbortController();
      actionControllerRef.current = controller;

      dispatch({ type: "ACTION_START", action });

      try {
        await execute(controller.signal);
      } catch (error) {
        if (isAbortError(error)) return;
        if (actionControllerRef.current !== controller) return;

        dispatch({
          type: "ACTION_ERROR",
          message: toUserMessage(error),
        });
      } finally {
        if (actionControllerRef.current === controller) {
          actionControllerRef.current = null;
        }
      }
    },
    [abortCurrentAction],
  );

  const submitSeatQuantity = useCallback(async () => {
    const current = state.data?.subscription.purchasedSeatQuantity;
    if (current == null) return;

    const parsed = Number.parseInt(state.targetSeatQuantityInput, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      dispatch({
        type: "ACTION_ERROR",
        message: "Bitte eine gültige Sitzanzahl größer oder gleich 1 eingeben.",
      });
      return;
    }

    if (parsed === current) {
      dispatch({
        type: "ACTION_ERROR",
        message: "Die Sitzanzahl hat sich nicht geändert.",
      });
      return;
    }

    const isIncrease = parsed > current;

    await runAction(
      isIncrease ? "increaseSeats" : "decreaseSeats",
      async (signal) => {
        if (isIncrease) {
          await increaseAccountSeats.execute(parsed, signal);
          await refresh("Sitzanzahl wurde aktualisiert.");
        } else {
          await decreaseAccountSeats.execute(parsed, signal);
          await refresh("Reduzierung wurde angefragt.");
        }
      },
    );
  }, [
    state.data,
    state.targetSeatQuantityInput,
    increaseAccountSeats,
    decreaseAccountSeats,
    refresh,
    runAction,
  ]);

  const cancelAtPeriodEnd = useCallback(async () => {
    await runAction("cancel", async (signal) => {
      await cancelAccountBilling.execute(true, signal);
      await refresh("Kündigung zum Periodenende wurde vorgemerkt.");
    });
  }, [cancelAccountBilling, refresh, runAction]);

  const reactivate = useCallback(async () => {
    await runAction("reactivate", async (signal) => {
      await reactivateAccountBilling.execute(signal);
      await refresh("Abonnement wurde reaktiviert.");
    });
  }, [reactivateAccountBilling, refresh, runAction]);

  const openBillingPortal = useCallback(async () => {
    await runAction("portal", async (signal) => {
      const result = await getBillingPortalLink.execute(
        window.location.href,
        signal,
      );

      window.location.assign(result.url);
    });
  }, [getBillingPortalLink, runAction]);

  const clearMessages = useCallback(() => {
    dispatch({ type: "CLEAR_MESSAGES" });
  }, []);

  const derived = useMemo(() => {
    const subscription = state.data?.subscription ?? null;

    return {
      subscription,
      pendingChange: state.data?.pendingChange ?? null,
      isBusy: state.isRefreshing || state.activeAction !== null,
      canSubmitSeatQuantity:
        !state.isInitialLoading &&
        !state.isRefreshing &&
        state.activeAction === null &&
        state.data !== null,
      isPortalBusy: state.activeAction === "portal",
      isCancelBusy: state.activeAction === "cancel",
      isReactivateBusy: state.activeAction === "reactivate",
      isSeatChangeBusy:
        state.activeAction === "increaseSeats" ||
        state.activeAction === "decreaseSeats",
    };
  }, [state]);

  return {
    state,
    derived,
    intents: {
      reload: refresh,
      setTargetSeatQuantityInput,
      submitSeatQuantity,
      cancelAtPeriodEnd,
      reactivate,
      openBillingPortal,
      clearMessages,
    },
  };
}
